import LaunchIcon from '@mui/icons-material/Launch';
import { Badge, Box, IconButton, Popover, Typography, alpha, styled } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTabs, { createTabItem } from '../../../core-components/theme/components/CustomTabs';
import { useNotificationModal } from '../hooks/useNotificationModal';
import NotificationList from './NotificationList';

const StyledPopover = styled(Popover)(({ theme }) => ({
    '& .MuiPopover-paper': {
        width: 600,
        height: '75vh',
        maxWidth: '100%',
        maxHeight: '80%',
        borderRadius: theme.spacing(2),
        boxShadow: theme.shadows[8],
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        padding: 10,
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            width: '92vw',
            padding: 3
        },
        '&::-webkit-scrollbar-track': {
            display: 'none',
          },
    },
  }));

const HeaderBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const ContentBox = styled(Box)({
    marginTop: '10px'
});

const tabStyles = {
    minHeight: 48,
    '& .MuiTabs-indicator': {
        backgroundColor: 'primary.main',
        height: 2,
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 500,
        color: 'text.secondary',
        transition: 'all 0.2s ease-in-out',
        '&.Mui-selected': {
            color: 'primary.main',
            fontWeight: 600,
        },
        '&:hover': {
            color: 'primary.main',
        },
    },
};

const headerButtonSx = {
    minWidth: 'auto',
    p: 0.5,
    ml: 1,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: 'action.hover',
        transform: 'scale(1.05)',
    },
};

const BadgeLabel: React.FC<{ label: string; count?: number }> = ({ label, count }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">{label}</Typography>
        {!!count && (
            <Badge
                badgeContent={count}
                color="primary"
                sx={{
                    '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        height: 18,
                        minWidth: 18,
                        position: 'static',
                        transform: 'none',
                    },
                }}
            >
                <Box />
            </Badge>
        )}
    </Box>
);

interface NotificationModalProps {
    onClose: () => void;
    anchorEl: HTMLButtonElement | null;
    open: boolean;
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
    open,
    anchorEl,
    onClose,
}) => {
    const { t } = useTranslation();
    const {
        notifications,
        readCount,
        unreadCount,
        allCount,
        error,
        isError,
        isFetchingNextPage,
        hasNextPage,
        activeTab,
        isLoading,
        fetchNextPage,
        setActiveTab,
        handleNavigateToNotifications,
        handleCloseModal,
        markNotification
    } = useNotificationModal({ initialTab: 'all', onClose });

    const contentBoxRef = useRef<HTMLDivElement>(null);

    const tabItems = useMemo(
        () => [
            createTabItem('all', <BadgeLabel label={t('notifications.modal.tabs.all', 'ALL')} count={allCount} />),
            createTabItem('unread', <BadgeLabel label={t('notifications.modal.tabs.unread', 'UNREAD')} count={unreadCount} />),
            createTabItem('read', <BadgeLabel label={t('notifications.modal.tabs.read', 'READ')} />),
        ],
        [allCount, unreadCount, readCount]
    );

    return (
        <StyledPopover
            ref={contentBoxRef}
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseModal}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <HeaderBox>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {t('notifications.title', 'Notifications')}
                </Typography>
                <IconButton
                    onClick={handleNavigateToNotifications}
                    sx={headerButtonSx}
                    size="small"
                    title={t('notifications.modal.goToCenter', 'Go to Notification Center')}
                >
                    <LaunchIcon fontSize="small" />
                </IconButton>
            </HeaderBox>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <CustomTabs
                    tabs={tabItems}
                    value={activeTab}
                    onChange={v => setActiveTab(v as 'all' | 'unread' | 'read')}
                    variant="underlined"
                    size="medium"
                    fullWidth
                    showContent={false}
                    tabsSx={tabStyles}
                />
            </Box>

            <ContentBox>
                <NotificationList
                    key={activeTab}
                    notifications={notifications}
                    isLoading={isLoading}
                    isError={isError}
                    error={error!}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    rootRef={contentBoxRef}
                    handleNotificationStatus={markNotification}
                />
            </ContentBox>
        </StyledPopover>
    );
};

export default NotificationModal;
